import type { ReactNode } from 'react';

import { cn } from '../lib/cn';
import { Badge } from './Badge';

export interface TimelineItem {
  id: string;
  /** The rendered wording. The record stores none; the server renders it. */
  description: ReactNode;
  timestamp: string;
  actor?: string | null;
  /**
   * Whether this entry is internal or shown to the client.
   *
   * NOT decoration. CR-25 is the leak class where somebody believed an entry was
   * internal. Where this is set, the entry renders a persistent TEXT label as well
   * as a colour, because colour alone is not sufficient to distinguish them.
   */
  visibility?: 'internal' | 'client';
  icon?: ReactNode;
}

export interface TimelineProps {
  items: TimelineItem[];
  /** How to format a timestamp. Defaults to the browser's locale, short form. */
  formatTimestamp?: (iso: string) => string;
  className?: string;
  emptyMessage?: string;
}

const defaultFormat = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

/**
 * The matter activity stream.
 *
 * An ordered list, so a screen reader announces "3 of 14" rather than presenting
 * fourteen unrelated divs. `<time datetime>` carries the machine-readable instant
 * alongside the human one.
 */
export function Timeline({
  items,
  formatTimestamp = defaultFormat,
  className,
  emptyMessage = 'Nothing has happened on this matter yet.',
}: TimelineProps) {
  if (items.length === 0) {
    return <p className="px-1 py-6 text-center text-sm text-ink-muted">{emptyMessage}</p>;
  }

  return (
    <ol className={cn('relative flex flex-col', className)}>
      {items.map((item, index) => {
        const last = index === items.length - 1;
        return (
          <li key={item.id} className="relative flex gap-3 pb-5 last:pb-0">
            {/* The rail. Decorative, so it is hidden from assistive tech. */}
            {!last && (
              <span
                className="absolute left-[11px] top-6 h-full w-px bg-line"
                aria-hidden="true"
              />
            )}

            <span
              className={cn(
                'relative z-10 mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border',
                item.visibility === 'internal'
                  ? 'border-internal/40 bg-internal-subtle text-internal'
                  : 'border-line bg-surface text-ink-subtle',
              )}
              aria-hidden="true"
            >
              {item.icon ?? <span className="h-1.5 w-1.5 rounded-full bg-current" />}
            </span>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-base text-ink">{item.description}</p>
                {item.visibility && (
                  <Badge
                    tone={item.visibility === 'internal' ? 'internal' : 'client-facing'}
                    size="sm"
                  >
                    {item.visibility === 'internal' ? 'Internal' : 'Client can see this'}
                  </Badge>
                )}
              </div>
              <p className="mt-0.5 text-xs text-ink-muted">
                <time dateTime={item.timestamp}>{formatTimestamp(item.timestamp)}</time>
                {item.actor && <span> · {item.actor}</span>}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
